/**
 * Midal rant — fires at the annual fiscal close when:
 *   • Tristan Midal (DAF) is hired,
 *   • The Midal auto-provisioning toggle was OFF during the year, AND
 *   • The company had at least one IS cash-flow incident
 *     (failed instalment shortfall OR took an emergency IS bank credit).
 *
 * Tone : pince-sans-rire / paternal warning. The DAF reminds the patron
 * that the toggle exists for exactly this kind of situation, and that
 * enabling it now would prevent a repeat next year.
 *
 * Returns a Messagerie payload (consumed by pushCustomMessage) or null
 * when the conditions are not met. Pure function — no side effects.
 *
 * 31/05/2026 — narrative bonus follow-up to the Midal perk.
 */

const MIN_INCIDENTS = 1;

/**
 * @param {object} state  live store state slice (must expose hiredSupport,
 *                        taxAutoProvisioningEnabled, marchettiIsIncidentsThisYear,
 *                        year, totalDays).
 * @returns {object|null} Messagerie payload OR null.
 */
export function buildMarchettiRantMessage(state) {
  if (!state || typeof state !== 'object') return null;
  const hasMarchetti = !!(state.hiredSupport && state.hiredSupport['role-pierre-marchetti']);
  if (!hasMarchetti) return null;
  if (state.taxAutoProvisioningEnabled) return null;
  const incidents = state.marchettiIsIncidentsThisYear || 0;
  if (incidents < MIN_INCIDENTS) return null;

  const year = state.year || 1;
  const totalDays = state.totalDays || 0;

  const incidentLabel = incidents > 1
    ? `${incidents} alertes IS`
    : `1 alerte IS`;

  const body =
    `Patron,\n\n` +
    `Petit débriefing fiscal de l'exercice ${year}.\n\n` +
    `Vous avez essuyé **${incidentLabel}** cette année — soit une trésorerie insuffisante au moment d'un acompte, soit un crédit bancaire de dernière minute pour éponger l'IS. Sans surprise : le provisionnement automatique TVA + IS est resté **désactivé** toute l'année.\n\n` +
    `Vous savez ce que je vais dire. Ce toggle, **je l'ai mis là exprès**. Il met de côté chaque jour, contrat par contrat, la TVA collectée et 25 % de l'IS prévisionnel. Le sur-provisionnement est rendu en fin d'année — vous n'y perdez pas un centime, vous y gagnez en sommeil.\n\n` +
    `Concrètement : **Fiscalité → Provisionnement fiscal automatique → ON**. C'est trois clics. Comparé à courir après votre banquier pour un crédit IS à 4,5 % au pire moment, le calcul est vite fait.\n\n` +
    `On en reparle en janvier. D'ici là, bonne fin d'année.\n\n` +
    `— Tristan Midal, votre DAF (qui aimerait dormir aussi)`;

  return {
    senderType: 'support',
    senderName: 'Tristan Midal (DAF)',
    senderRole: 'role-pierre-marchetti',
    tone: 'warning',
    kind: 'info',
    subject: `Bilan IS ${year} — on en reparle ?`,
    body,
    ttlDays: 30,
    receivedDay: totalDays,
  };
}
